import { type FC } from "react";

import { AnomalyBadge, ErrorKindBadge } from "./badges";
import {
  type AnomalyFlag,
  type StepEntity,
  type StepKind,
  type WakeSummary,
  LOCK_WAIT_VISIBLE_MS,
  fmtDuration,
  fmtHitPct,
  fmtTime,
  fmtTokens,
  formatStepErrorLabel,
  formatWakeReplayBadge,
  formatWakeReplayTitle,
  hitPctColor,
  hitPctTooltip,
  modelRefTail,
  shortSha,
  totalWallClockMs,
} from "./observabilityModel";
import { StepList } from "./StepList";

export const WakeDurationCell: FC<{
  turnMs: number | null;
  lockWaitMs: number | null;
}> = ({ turnMs, lockWaitMs }) => {
  const total = totalWallClockMs(turnMs, lockWaitMs);
  const showLock =
    typeof lockWaitMs === "number" && lockWaitMs >= LOCK_WAIT_VISIBLE_MS;
  return (
    <span className="flex w-14 shrink-0 flex-col items-end leading-tight">
      <span className="text-slate-400">{fmtDuration(total)}</span>
      {showLock && (
        <span
          className="text-[9px] uppercase tracking-wider text-amber-600 dark:text-amber-400"
          title={`${fmtDuration(lockWaitMs)} waiting on channel mutex · ${fmtDuration(turnMs)} generateText`}
        >
          L:{fmtDuration(lockWaitMs)}
        </span>
      )}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Wake row — tier 1. Shows a single wake with anomaly badges. Expandable.
// ---------------------------------------------------------------------------

interface WakeRowProps {
  wake: WakeSummary;
  anomalies: AnomalyFlag[];
  hitPct: number | null;
  /** Prior wake's hit pct, used to grade the current cell's color by drop magnitude. */
  priorHitPct: number | null | undefined;
  expanded: boolean;
  onToggle: () => void;
  steps: StepEntity[] | undefined;
  onOpenStep: (stepId: string) => void;
  onDiffPrior?: () => Promise<void>;
}

export const STEP_KIND_COLORS: Record<StepKind, string> = {
  actor_wake: "bg-sky-500",
  consolidation_only_wake: "bg-violet-500",
  external_compact_seed: "bg-rose-500",
};

export const STEP_KIND_LABELS: Record<StepKind, string> = {
  actor_wake: "wake",
  consolidation_only_wake: "consol",
  external_compact_seed: "compact",
};

export const WakeRow: FC<WakeRowProps> = ({
  wake,
  anomalies,
  hitPct,
  priorHitPct,
  expanded,
  onToggle,
  steps,
  onOpenStep,
  onDiffPrior,
}) => {
  const hitColor = hitPctColor(hitPct, priorHitPct);
  const hasProblem =
    anomalies.includes("cache_miss") ||
    anomalies.includes("error") ||
    anomalies.includes("cache_hit_drop");

  return (
    <div
      className={`border-b border-slate-200 transition-colors dark:border-slate-800/60 ${
        hasProblem ? "bg-rose-500/5" : ""
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full min-w-0 items-center gap-2 px-4 py-2 text-left font-mono text-[11px] transition-colors hover:bg-emerald-400/[0.04]"
      >
        <span
          className={`h-1.5 w-1.5 shrink-0 rounded-full ${STEP_KIND_COLORS[wake.stepKind]}`}
        />
        <span className="w-16 shrink-0 tabular-nums text-slate-500">
          {fmtTime(wake.startedAt)}
        </span>
        <span
          className={`w-14 shrink-0 uppercase tracking-[0.15em] ${hasProblem ? "text-rose-700 dark:text-rose-300" : "text-slate-600 dark:text-slate-400"}`}
        >
          {STEP_KIND_LABELS[wake.stepKind]}
        </span>
        {/* Phase hidden on narrow — chat-mode wakes are ~always "act"; the
            column just pushes the signal columns off-screen on mobile. */}
        <span className="hidden w-14 shrink-0 text-slate-500 sm:inline">
          {wake.phase ?? "—"}
        </span>
        <span className="w-12 shrink-0 text-right tabular-nums text-slate-700 dark:text-slate-300">
          {fmtTokens(wake.totalInputTokens)}
        </span>
        <span
          className={`w-12 shrink-0 text-right tabular-nums ${hitColor}`}
          title={hitPctTooltip(hitPct, priorHitPct)}
        >
          {fmtHitPct(hitPct)}
        </span>
        <WakeDurationCell
          turnMs={wake.totalDurationMs}
          lockWaitMs={wake.lockWaitMs}
        />
        <span className="min-w-0 flex-1 overflow-hidden">
          <span className="flex flex-wrap gap-1">
            {wake.errorKind !== null && (
              <ErrorKindBadge label={formatStepErrorLabel(wake)} />
            )}
            {wake.usedReplay && (
              <span
                className="rounded-sm bg-indigo-100 px-1 py-px font-mono text-[9px] uppercase tracking-wider text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300"
                title={formatWakeReplayTitle(wake)}
              >
                {formatWakeReplayBadge(wake)}
              </span>
            )}
            {anomalies
              .filter((flag) => !(flag === "error" && wake.errorKind !== null))
              .map((flag) => (
                <AnomalyBadge key={flag} flag={flag} />
              ))}
          </span>
        </span>
        <span
          className={`shrink-0 text-[10px] ${expanded ? "text-emerald-400" : "text-slate-600"}`}
        >
          {expanded ? "▾" : "▸"}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-emerald-500/20 bg-slate-50/60 px-4 py-2 dark:border-emerald-400/10 dark:bg-slate-950/60">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pb-2 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500">
            <span className="flex min-w-0 max-w-full items-baseline gap-1">
              MODEL:
              <span className="min-w-0 truncate normal-case tracking-normal text-slate-700 dark:text-slate-300">
                {modelRefTail(wake.modelRef)}
              </span>
            </span>
            <span>
              PREFIX:{" "}
              <span className="tracking-normal text-slate-400">
                {shortSha(wake.prefixSha)}
              </span>
            </span>
            <span>
              SYS:{" "}
              <span className="tracking-normal text-slate-400">
                {shortSha(wake.systemPromptSha)}
              </span>
            </span>
            <span>
              TOOLS:{" "}
              <span className="tracking-normal text-slate-400">
                {shortSha(wake.toolsSha)}
              </span>
            </span>
            {onDiffPrior && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  void onDiffPrior();
                }}
                className="ml-auto shrink-0 border border-emerald-400/30 bg-white/60 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-emerald-700/90 transition-colors hover:border-emerald-400/60 hover:text-emerald-700 dark:bg-slate-900/60 dark:text-emerald-300/80 dark:hover:text-emerald-300"
              >
                [ DIFF vs PRIOR ]
              </button>
            )}
          </div>
          {steps ? (
            <StepList steps={steps} onOpenStep={onOpenStep} />
          ) : (
            <div className="py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-700/80 dark:text-emerald-300/60">
              [ LOADING STEPS… ]
            </div>
          )}
        </div>
      )}
    </div>
  );
};
